<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        {{ $vuetify.lang.t('$vuetify.subscription_plans.update_title') }}
      </v-card-title>

      <v-card-text v-if="pageLoading">
        <subscription-plan-form-skeleton />
      </v-card-text>

      <v-card-text v-else-if="form">
        <subscription-plan-form
          :initial-data="form"
          :existing-plans="subscriptionPlans"
          :submit-text="$vuetify.lang.t('$vuetify.common.update_btn')"
          @submit="handleUpdate"
          @cancel="goBack"
        />
      </v-card-text>

      <v-card-text v-else class="text-center py-8">
        <v-icon size="48" color="grey lighten-1" class="mb-3">
          mdi-alert-circle-outline
        </v-icon>

        <div class="text-subtitle-1 font-weight-medium mb-2">
          {{ $vuetify.lang.t('$vuetify.subscription_plans.messages.no_plans_found') }}
        </div>

        <div class="text-body-2 grey--text mb-4">
          {{ $vuetify.lang.t('$vuetify.subscription_plans.messages.not_found_desc') }}
        </div>
      </v-card-text>
    </v-card>

    <title-match-confirmation-dialog
      v-model="updateDialog"
      :match-text="updateMatchTitle"
      :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
      :message="$vuetify.lang.t('$vuetify.subscription_plans.update_confirm_message')"
      :input-label="$vuetify.lang.t('$vuetify.subscription_plans.plan_title_input_label')"
      :confirm-button-text="$vuetify.lang.t('$vuetify.common.confirm')"
      :cancel-button-text="$vuetify.lang.t('$vuetify.common.cancel_btn')"
      :confirm-btn-loading="saving"
      @onConfirmation="confirmUpdate"
      @onCancel="closeUpdateDialog"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  LOAD_SUBSCRIPTION_PLAN_BY_ID,
  UPDATE_SUBSCRIPTION_PLAN,
  ADD_ALERT,
  LOAD_ALL_SUBSCRIPTION_PLANS
} from "@/store/_actiontypes";

import SubscriptionPlanForm from "@/components/SubscriptionPlans/SubscriptionPlanForm.vue";
import TitleMatchConfirmationDialog from "@/components/common/TitleMatchConfirmationDialog.vue";
import SubscriptionPlanFormSkeleton from "@/components/SubscriptionPlans/SubscriptionPlanFormSkeleton.vue";
import _ from 'lodash';
import getEnv from "@/config/env";

export default {
  name: "SubscriptionPlanEdit",

  components: {
    SubscriptionPlanForm,
    SubscriptionPlanFormSkeleton,
    TitleMatchConfirmationDialog,
  },

  data() {
    return {
      saving: false,
      form: null,
      pageLoading: false,
      updateDialog: false,
      pendingFormData: null,
    };
  },

  computed: {
    ...mapGetters("subscription_plans", [
      "subscriptionPlans",
      "subscriptionPlan",
      "subscriptionPlansErrorMessage",
      "subscriptionPlansSuccessMessage"
    ]),

    currentPlanId() {
      return this.$route.params.id;
    },

    updateMatchTitle() {
      return this.pendingFormData?.title || this.form?.title || "";
    },
  },

  async created() {
    this.pageLoading = true;

    try {
      if (!this.subscriptionPlans?.length) {
        let subscription = null;

        if (getEnv("VUE_APP_CAPTCHA_ENABLED")) {
          subscription = await this.$recaptcha.execute("subscription");
        }

        await this.loadPlans({ recaptcha_token: subscription });
      }

      const currentItem = await this.fetchSubscriptionPlan(this.currentPlanId);

      this.form = _.cloneDeep(currentItem);

    } catch (error) {
      console.log("error",error)

      this.addAlert({
        message: this.$vuetify.lang.t(
          "$vuetify.subscription_plans.messages.load_failed"
        ),
        color: "error",
      });

      this.form = null;
    } finally {
      this.pageLoading = false;
    }
  },

  methods: {
    ...mapActions("subscription_plans", {
      fetchSubscriptionPlan: LOAD_SUBSCRIPTION_PLAN_BY_ID,
      updatePlan: UPDATE_SUBSCRIPTION_PLAN,
      loadPlans: LOAD_ALL_SUBSCRIPTION_PLANS,
    }),

    ...mapActions("alert", {
      addAlert: ADD_ALERT,
    }),

    goBack() {
      this.$router.push({ name: "SubscriptionPlans" });
    },

    handleUpdate(formData) {
      this.pendingFormData = _.cloneDeep(formData);
      this.updateDialog = true;
    },

    closeUpdateDialog() {
      this.updateDialog = false;
      this.pendingFormData = null;
    },

    async confirmUpdate() {
      if (!this.pendingFormData) return;

      this.saving = true;

      try {
        await this.updatePlan(this.pendingFormData);

        this.addAlert({
          message: this.subscriptionPlansSuccessMessage
            || this.$vuetify.lang.t('$vuetify.subscription_plans.messages.update_success'),
          color: "success",
        });

        this.closeUpdateDialog();
        this.goBack();

      } catch (error) {

        this.addAlert({
          message: this.subscriptionPlansErrorMessage
            || this.$vuetify.lang.t('$vuetify.subscription_plans.messages.update_failed'),
          color: "error",
        });

      } finally {
        this.saving = false;
      }
    },
  },
};
</script>